/**
 * IMF CPL Parser v3 — browser port of IMF_CPL_Parser_v3.py (logic aligned with current Python).
 * Client-side only; no upload to a server.
 *
 * Strapi / React: raw HTML embeds often inject markup after load and may not execute <script>
 * inside the embed. Prefer loading this file once from your front-end layout (or a Custom Code
 * block), then paste only the widget markup. Retries + MutationObserver still wire the UI when
 * the widget mounts late.
 */
(function () {
  'use strict';

  const XMLNS_ATTR_NS = 'http://www.w3.org/2000/xmlns/';

  const ulMappings = {
    'urn:smpte:ul:060e2b34.04010106.04010101.03030000': 'Rec. 709 / BT.709',
    'urn:smpte:ul:060e2b34.0401010d.04010101.03030000': 'Rec. 709 / BT.709',
    'urn:smpte:ul:060e2b34.04010106.04010101.03040000': 'Rec. 2020 / BT.2020',
    'urn:smpte:ul:060e2b34.0401010d.04010101.03040000': 'Rec. 2020 / BT.2020',
    'urn:smpte:ul:060e2b34.04010106.04010101.03050000': 'P3',
    'urn:smpte:ul:060e2b34.04010101.04010101.01020000': 'Rec. 709 Transfer',
    'urn:smpte:ul:060e2b34.0401010d.04010101.01020000': 'Rec. 709 Transfer',
    'urn:smpte:ul:060e2b34.04010101.04010101.01030000': 'Rec. 2020 Transfer',
    'urn:smpte:ul:060e2b34.0401010d.04010101.01030000': 'Rec. 2020 Transfer',
    'urn:smpte:ul:060e2b34.04010101.04010101.01040000': 'ST.2084 (PQ)',
    'urn:smpte:ul:060e2b34.0401010d.04010101.010a0000': 'ST.2084 (PQ)',
    'urn:smpte:ul:060e2b34.04010101.04010101.01050000': 'HLG',
    'urn:smpte:ul:060e2b34.0401010d.04010101.01050000': 'HLG',
  };

  const masteringDisplays = new Map([
    [['35400', '14600', '8500', '39850', '6550', '2300', '15635', '16450'].join(','), 'BT.2020'],
    [['34000', '16000', '13250', '34500', '7500', '3000', '15635', '16450'].join(','), 'P3-D65'],
    [['32000', '16500', '6800', '32000', '16500', '8400', '15635', '16450'].join(','), 'DCI-P3'],
  ]);

  const masteringGamutLabels = {
    'BT.2020': 'Rec. 2020',
    'P3-D65': 'P3-D65',
    'DCI-P3': 'DCI-P3',
  };

  const whitePointChromaticity = new Map([['15635,16450', 'D65']]);

  const colorPrimariesWhitePoint = {
    'urn:smpte:ul:060e2b34.04010106.04010101.03030000': 'D65',
    'urn:smpte:ul:060e2b34.0401010d.04010101.03030000': 'D65',
    'urn:smpte:ul:060e2b34.04010106.04010101.03040000': 'D65',
    'urn:smpte:ul:060e2b34.0401010d.04010101.03040000': 'D65',
    'urn:smpte:ul:060e2b34.04010106.04010101.03050000': 'D65',
  };

  const J2K_UL_PROFILES = {
    '03010614': { name: 'Master DCP', mainlevel: 7, sublevel: 0, encoding: 'Lossless' },
    '03010400': { name: 'HD Lossless', mainlevel: 4, sublevel: 0, encoding: 'Lossless' },
    '03010600': { name: 'Distribution', mainlevel: 6, sublevel: 4, encoding: 'Lossy' },
    '03010313': { name: 'Distribution', mainlevel: 6, sublevel: 4, encoding: 'Lossy' },
    '0301030e': { name: 'Main Profile', mainlevel: 5, sublevel: 3, encoding: 'Lossy' },
    '03010801': { name: 'High Throughput J2K (HTJ2K)', mainlevel: null, sublevel: null, encoding: 'Variable' },
  };

  const disneyEltLanguageCodes = {
    en: 'ENG',
    es: 'SPA',
    fr: 'FRA',
    de: 'GER',
    it: 'ITA',
    pt: 'POR',
    ja: 'JPN',
    ko: 'KOR',
    zh: 'ZHO',
    cmn: 'ZHO',
    yue: 'YUE',
    hi: 'HIN',
    ar: 'ARA',
    ru: 'RUS',
    nl: 'DUT',
    sv: 'SWE',
    no: 'NOR',
    da: 'DAN',
    fi: 'FIN',
    pl: 'POL',
    tr: 'TUR',
    th: 'THA',
    vi: 'VIE',
    id: 'IND',
    ms: 'MAY',
    tl: 'TGL',
    he: 'HEB',
    el: 'GRE',
    cs: 'CZE',
    sk: 'SLK',
    hu: 'HUN',
    ro: 'RUM',
    uk: 'UKR',
    las: 'LAS',
  };

  function humanizeMasteringGamut(storedName) {
    return masteringGamutLabels[storedName] || storedName;
  }

  function gamutLabelForPrimaries(primaries6) {
    const rgb = primaries6.slice(0, 6).join(',');
    for (const [keyStr, name] of masteringDisplays) {
      const key = keyStr.split(',');
      if (key.slice(0, 6).join(',') === rgb) return humanizeMasteringGamut(name);
    }
    return 'Custom';
  }

  function gamutLabelForFullKey(key8) {
    const full = key8.join(',');
    if (masteringDisplays.has(full)) return humanizeMasteringGamut(masteringDisplays.get(full));
    return gamutLabelForPrimaries(key8);
  }

  function primaryLanguageSubtag(rfc5646) {
    if (rfc5646 == null || !String(rfc5646).trim()) return '';
    return String(rfc5646).trim().split('-', 1)[0].toLowerCase();
  }

  function disneyEltCodeForTag(rfc5646) {
    if (rfc5646 == null || !String(rfc5646).trim()) return null;
    const full = String(rfc5646).trim().toLowerCase();
    if (Object.prototype.hasOwnProperty.call(disneyEltLanguageCodes, full))
      return disneyEltLanguageCodes[full];
    const primary = primaryLanguageSubtag(rfc5646);
    return primary ? disneyEltLanguageCodes[primary] || null : null;
  }

  function formatDisneyEltForTag(rfc5646) {
    const code = disneyEltCodeForTag(rfc5646);
    return code || 'n/a';
  }

  function getText(el) {
    if (!el) return null;
    let s = '';
    for (let i = 0; i < el.childNodes.length; i++) {
      const n = el.childNodes[i];
      if (n.nodeType === Node.TEXT_NODE) s += n.nodeValue;
    }
    const t = s.trim();
    if (t) return t;
    if (el.children.length === 0) return (el.textContent || '').trim() || null;
    return null;
  }

  function* walkElements(el) {
    yield el;
    for (let i = 0; i < el.children.length; i++) yield* walkElements(el.children[i]);
  }

  function findDesc(root, localName, uri) {
    for (const e of walkElements(root)) {
      if (e.localName === localName && (uri == null || e.namespaceURI === uri)) return e;
    }
    return null;
  }

  function findAllDesc(root, localName, uri) {
    const out = [];
    for (const e of walkElements(root)) {
      if (e.localName === localName && (uri == null || e.namespaceURI === uri)) out.push(e);
    }
    return out;
  }

  function findChild(parent, localName, uri) {
    for (let i = 0; i < parent.children.length; i++) {
      const c = parent.children[i];
      if (c.localName === localName && (uri == null || c.namespaceURI === uri)) return c;
    }
    return null;
  }

  /** First element in document order with local tag name (matches Python find_element_by_name / root.iter). */
  function findElementByName(root, name) {
    for (const el of walkElements(root)) {
      if (el.localName === name) return el;
    }
    return null;
  }

  /** Direct children with local tag name (matches Python find_elements_by_name). */
  function findElementsByName(element, name) {
    const out = [];
    for (let i = 0; i < element.children.length; i++) {
      const c = element.children[i];
      if (c.localName === name) out.push(c);
    }
    return out;
  }

  /** First direct child with local tag name (matches Python find_child_by_name). */
  function findChildByName(element, name) {
    for (let i = 0; i < element.children.length; i++) {
      const c = element.children[i];
      if (c.localName === name) return c;
    }
    return null;
  }

  /** True when any element local name contains "isxd" (IMF_CPL_Parser_v3.py has_isxd_track). */
  function hasIsxdTrack(root) {
    for (const el of walkElements(root)) {
      if (el.localName && el.localName.toLowerCase().indexOf('isxd') !== -1) return true;
    }
    return false;
  }

  function parseEditRate(text) {
    if (text == null) return null;
    const raw = String(text).trim();
    if (!raw) return null;
    const parts = raw.indexOf('/') !== -1 ? raw.split('/') : raw.split(/\s+/);
    if (parts.length !== 2) return null;
    const num = parseInt(parts[0], 10);
    const den = parseInt(parts[1], 10);
    if (!Number.isFinite(num) || !Number.isFinite(den) || den === 0) return null;
    return [num, den];
  }

  function formatFps(num, den) {
    const fps = num / den;
    if (Math.abs(fps - Math.round(fps)) < 1e-9) return `${Math.round(fps)} fps`;
    return `${fps.toFixed(3)} fps`;
  }

  function isRec709Encoding(colorPrimariesUl, transferCharacteristicUl) {
    const cp709 = [
      'urn:smpte:ul:060e2b34.04010106.04010101.03030000',
      'urn:smpte:ul:060e2b34.0401010d.04010101.03030000',
    ];
    const tc709 = [
      'urn:smpte:ul:060e2b34.04010101.04010101.01020000',
      'urn:smpte:ul:060e2b34.0401010d.04010101.01020000',
    ];
    return cp709.indexOf(colorPrimariesUl) !== -1 && tc709.indexOf(transferCharacteristicUl) !== -1;
  }

  function pushMasteringDisplayMissing(lines, rec709Encoding, fallbackLabel) {
    if (rec709Encoding) {
      lines.push('Mastering Display Primaries: n/a');
      lines.push('Mastering Display White Point: n/a');
    } else {
      lines.push(fallbackLabel || 'Mastering Display Color Space: Not found');
    }
  }

  function collectResolutionConsistency(root) {
    const imageDescriptors = [];
    for (const el of walkElements(root)) {
      if (el.localName === 'RGBADescriptor' || el.localName === 'CDCIDescriptor') {
        imageDescriptors.push(el);
      }
    }
    if (!imageDescriptors.length) return null;

    const fieldNames = ['ActiveWidth', 'ActiveHeight', 'ActiveXOffset', 'ActiveYOffset'];
    const fieldLabels = {
      ActiveWidth: 'Active Width',
      ActiveHeight: 'Active Height',
      ActiveXOffset: 'Active X Offset',
      ActiveYOffset: 'Active Y Offset',
    };
    const summary = [];
    for (let fi = 0; fi < fieldNames.length; fi++) {
      const field = fieldNames[fi];
      const values = [];
      let missingCount = 0;
      for (let di = 0; di < imageDescriptors.length; di++) {
        const valueElem = findChildByName(imageDescriptors[di], field);
        const t = valueElem ? getText(valueElem) : '';
        if (t) values.push(t);
        else missingCount++;
      }
      const uniqueValues = [];
      for (let vi = 0; vi < values.length; vi++) {
        if (uniqueValues.indexOf(values[vi]) === -1) uniqueValues.push(values[vi]);
      }
      const hasMismatch =
        uniqueValues.length > 1 || (missingCount > 0 && imageDescriptors.length > 1);
      let displayValue = 'Not found';
      if (uniqueValues.length === 1) displayValue = uniqueValues[0];
      else if (uniqueValues.length > 1) displayValue = uniqueValues.join(', ');
      let warning = null;
      if (hasMismatch) {
        warning = uniqueValues.length
          ? `WARNING: ${fieldLabels[field]} mismatch across image essence descriptors`
          : `WARNING: ${fieldLabels[field]} missing across image essence descriptors`;
      }
      summary.push({ label: fieldLabels[field], displayValue: displayValue, warning: warning });
    }
    return summary;
  }

  function getJ2kProfileFromUl(pictureCompressionUl) {
    if (!pictureCompressionUl) return null;
    const parts = String(pictureCompressionUl).split('.');
    if (parts.length < 4) return null;
    let ulEnding = parts[parts.length - 1].trim();
    if (J2K_UL_PROFILES[ulEnding]) return J2K_UL_PROFILES[ulEnding];
    if (ulEnding.length >= 6) {
      const suffix = ulEnding.slice(-6);
      if (J2K_UL_PROFILES[suffix]) return J2K_UL_PROFILES[suffix];
    }
    return null;
  }

  function parseJ2kCodingStyleDefault(hexString) {
    if (!hexString) return null;
    try {
      const hexClean = String(hexString).replace(/\s+/g, '').toUpperCase();
      if (hexClean.length < 6) return null;
      const scodByte = parseInt(hexClean.slice(0, 2), 16);
      const lossless = Boolean(scodByte & 0x01);
      const mainlevel = parseInt(hexClean.slice(2, 4), 16);
      const sublevel = parseInt(hexClean.slice(4, 6), 16);
      return { mainlevel: mainlevel, sublevel: sublevel, losslessFlag: lossless };
    } catch (e) {
      return null;
    }
  }

  function findJpeg2000Subdescriptor(root) {
    for (const el of walkElements(root)) {
      if (el.localName === 'JPEG2000SubDescriptor') return el;
    }
    return null;
  }

  function findPictureCompressionForJ2k(root, j2kDescriptor) {
    for (const elem of walkElements(root)) {
      if (elem.localName !== 'SubDescriptors') continue;
      for (let i = 0; i < elem.children.length; i++) {
        if (elem.children[i] === j2kDescriptor) {
          const rgba = elem.parentElement;
          if (!rgba) return null;
          const pc = findChildByName(rgba, 'PictureCompression');
          return pc ? getText(pc) : null;
        }
      }
    }
    return null;
  }

  function determineLosslessStatus(j2kDescriptor) {
    if (!j2kDescriptor) return 'Unknown';
    const quantDefault = findChildByName(j2kDescriptor, 'QuantizationDefault');
    if (quantDefault && getText(quantDefault)) return 'Lossy';
    const codingStyle = findChildByName(j2kDescriptor, 'CodingStyleDefault');
    if (codingStyle && getText(codingStyle)) {
      const parsed = parseJ2kCodingStyleDefault(getText(codingStyle));
      if (parsed && parsed.losslessFlag) return 'Lossless';
    }
    return 'Unknown';
  }

  function checkDisneySpecCompliance(mainlevel, sublevel, isLossless, isHtj2k) {
    if (mainlevel == null || sublevel == null || isLossless == null) return null;
    if (isHtj2k && isLossless) return 'High Throughput Lossless';
    if (mainlevel === 7 && sublevel === 0 && isLossless) return 'Master DCP';
    if (mainlevel === 6 && (sublevel === 3 || sublevel === 4) && !isLossless) return 'Distribution';
    if (mainlevel === 4 && sublevel === 0 && isLossless) return 'HD Lossless';
    if (mainlevel === 5 && sublevel === 3 && !isLossless) return 'Rec.709 Lossy';
    return null;
  }

  function disneySpecReportLabel(disneySpec) {
    if (disneySpec === 'Master DCP' || disneySpec === 'HD Lossless' || disneySpec === 'High Throughput Lossless') {
      return 'Yes matches Disney Archival Profile';
    }
    if (disneySpec === 'Distribution' || disneySpec === 'Rec.709 Lossy') {
      return 'Yes, matches Disney Distribution Profile';
    }
    return 'Does not match a Disney spec profile';
  }

  function extractJ2kEncodingProfile(root, ns) {
    const j2kDescriptor = findJpeg2000Subdescriptor(root);
    if (!j2kDescriptor) return null;

    const result = { profileName: 'Unknown', isHtj2k: false };
    const rsizElem = findChildByName(j2kDescriptor, 'Rsiz');
    if (rsizElem && getText(rsizElem)) {
      const rsizText = getText(rsizElem);
      const rsizVal = parseInt(rsizText, 10);
      if (Number.isFinite(rsizVal)) result.rsiz = rsizVal;
      else result.rsiz = rsizText;
    }

    const pictureCompressionUl = findPictureCompressionForJ2k(root, j2kDescriptor);
    if (pictureCompressionUl) {
      result.pictureCompressionUl = pictureCompressionUl;
      const ulProfile = getJ2kProfileFromUl(pictureCompressionUl);
      if (ulProfile) {
        result.profileName = ulProfile.name || result.profileName;
        result.isHtj2k = ulProfile.name === 'High Throughput J2K (HTJ2K)';
        if (ulProfile.mainlevel != null) {
          result.mainlevel = ulProfile.mainlevel;
          result.sublevel = ulProfile.sublevel;
        }
        if (ulProfile.encoding === 'Lossless' || ulProfile.encoding === 'Lossy') {
          result.encoding = ulProfile.encoding;
        }
      }
    }

    if (!('mainlevel' in result)) {
      const codingStyle = findChildByName(j2kDescriptor, 'CodingStyleDefault');
      if (codingStyle && getText(codingStyle)) {
        const parsed = parseJ2kCodingStyleDefault(getText(codingStyle));
        if (parsed) {
          result.mainlevel = parsed.mainlevel;
          result.sublevel = parsed.sublevel;
        }
      }
    }

    if (!('encoding' in result)) {
      result.encoding = determineLosslessStatus(j2kDescriptor);
    }

    if (result.sublevel === 0 || result.encoding === 'Lossless') {
      result.profileName = 'Disney Archival';
    } else if (result.encoding === 'Lossy') {
      result.profileName = 'Disney Distribution';
    }

    const isLossless = result.encoding === 'Lossless';
    if ('mainlevel' in result && 'sublevel' in result) {
      result.disneySpec = checkDisneySpecCompliance(
        result.mainlevel,
        result.sublevel,
        isLossless,
        result.isHtj2k
      );
    } else {
      result.disneySpec = null;
    }

    return result;
  }

  function mergeXmlnsFromDocument(root, ns) {
    if (!ns) return ns;
    const merged = { ...ns };
    function visit(el) {
      if (el.attributes) {
        for (let i = 0; i < el.attributes.length; i++) {
          const attr = el.attributes[i];
          const name = attr.name;
          if (name.startsWith('xmlns:')) {
            const prefix = name.slice(6);
            if (prefix && merged[prefix] === undefined) merged[prefix] = attr.value;
          } else if (attr.namespaceURI === XMLNS_ATTR_NS && attr.localName && attr.localName !== 'xmlns') {
            if (merged[attr.localName] === undefined) merged[attr.localName] = attr.value;
          }
        }
      }
      for (let j = 0; j < el.children.length; j++) visit(el.children[j]);
    }
    visit(root);
    return merged;
  }

  function buildNsFromRoot(root) {
    let cplUri = root.namespaceURI || null;
    if (!cplUri && root.lookupNamespaceURI && root.lookupNamespaceURI(''))
      cplUri = root.lookupNamespaceURI('');
    if (!cplUri && root.getAttribute) {
      const def = root.getAttribute('xmlns');
      if (def) cplUri = def;
    }
    if (root.tagName && String(root.tagName).includes(':')) {
      const parts = String(root.tagName).split(':');
      if (parts.length === 2 && root.lookupNamespaceURI(parts[0]))
        cplUri = root.lookupNamespaceURI(parts[0]);
    }

    const ns = {
      cpl: cplUri || null,
      r0: 'http://www.smpte-ra.org/reg/395/2014/13/1/aaf',
      r1: 'http://www.smpte-ra.org/reg/335/2012',
      r2: 'http://www.smpte-ra.org/reg/2003/2012',
      r3: 'http://sandflow.com/ns/SMPTEST2001-1/baseline',
      r4: 'http://www.smpte-ra.org/reg/395/2014/14/9',
      r5: 'http://www.smpte-ra.org/reg/335/2012/14/9',
      reg2012: 'http://www.smpte-ra.org/reg/335/2012/14/9',
      reg2014: 'http://www.smpte-ra.org/reg/395/2014/14/9',
      const: 'http://www.smpte-ra.org/ns/2067-2/2020',
      cc: 'http://www.smpte-ra.org/schemas/2067-2/2016',
    };
    return mergeXmlnsFromDocument(root, ns);
  }

  function getCompositionEditRate(root, ns) {
    if (ns && ns.cpl) {
      let er = findChild(root, 'EditRate', ns.cpl);
      if (!er) er = findDesc(root, 'EditRate', ns.cpl);
      return er;
    }
    return findDesc(root, 'EditRate', null);
  }

  function findEssenceDescriptorById(root, ns, encodingId) {
    if (!encodingId) return null;
    const uri = ns ? ns.cpl : null;
    for (const ed of findAllDesc(root, 'EssenceDescriptor', uri)) {
      const idEl = findChild(ed, 'Id', uri);
      if (idEl && getText(idEl) === encodingId) return ed;
    }
    return null;
  }

  function findSequenceListOnSegment(segment, ns) {
    const uri = ns ? ns.cpl : null;
    return findDesc(segment, 'SequenceList', uri);
  }

  function findMainImageSequence(sequenceList, ns) {
    if (ns) {
      const el =
        findDesc(sequenceList, 'MainImageSequence', ns.const) ||
        findDesc(sequenceList, 'MainImageSequence', ns.cc);
      if (el) return el;
    }
    for (let i = 0; i < sequenceList.children.length; i++) {
      const c = sequenceList.children[i];
      if (c.localName === 'MainImageSequence') return c;
    }
    return null;
  }

  function findAllMainAudioSequences(sequenceList, ns) {
    if (ns) {
      const a = findAllDesc(sequenceList, 'MainAudioSequence', ns.const);
      const b = findAllDesc(sequenceList, 'MainAudioSequence', ns.cc);
      if (a.length || b.length) return a.concat(b);
    }
    const out = [];
    for (let i = 0; i < sequenceList.children.length; i++) {
      const c = sequenceList.children[i];
      if (c.localName === 'MainAudioSequence') out.push(c);
    }
    return out;
  }

  /**
   * Bit depth: namespace-agnostic search (IMF_CPL_Parser_v2.1.py find_bit_depth).
   * 1) RGBAComponentSize anywhere  2) first non-zero ComponentSize under RGBAComponent
   * 3) any ComponentSize as last resort
   */
  function findBitDepth(root) {
    for (const elem of walkElements(root)) {
      if (elem.localName === 'RGBAComponentSize') {
        const t = getText(elem);
        if (t) return t;
      }
    }
    for (const comp of walkElements(root)) {
      if (comp.localName === 'RGBAComponent') {
        const compSize = findChildByName(comp, 'ComponentSize');
        if (compSize) {
          const t = getText(compSize);
          if (t && t !== '0') return t;
        }
      }
    }
    for (const elem of walkElements(root)) {
      if (elem.localName === 'ComponentSize') {
        const t = getText(elem);
        if (t) return t;
      }
    }
    return null;
  }

  /**
   * Non-fatal checks aligned with IMF_CPL_Parser_v2.2.py expectations (warnings only).
   * Fatal checks (well-formed XML, CompositionPlaylist root) are in parseCplDocumentResult.
   */
  function collectCplWarnings(root, ns) {
    const warnings = [];
    function findCpl(local) {
      if (ns && ns.cpl) return findDesc(root, local, ns.cpl);
      return findDesc(root, local, null);
    }
    if (!findCpl('Id')) warnings.push('Id: not found (composition identifier).');
    if (!findCpl('ContentTitleText') && !findCpl('ContentTitle'))
      warnings.push('Neither ContentTitleText nor ContentTitle was found.');
    if (!findCpl('SegmentList'))
      warnings.push('SegmentList: not found; timeline duration and audio summary may be missing.');
    const er = getCompositionEditRate(root, ns);
    if (!er || !getText(er))
      warnings.push('EditRate: not found at composition level; run time cannot be computed from CPL.');
    return warnings;
  }

  function buildReport(filename, root, ns) {
    const lines = [];
    const base = filename.split(/[/\\]/).pop() || filename;

    function findCpl(local) {
      if (ns && ns.cpl) return findDesc(root, local, ns.cpl);
      return findDesc(root, local, null);
    }

    lines.push(`Parsed File: ${base}`);
    lines.push('');

    const idEl = findCpl('Id');
    if (idEl) lines.push(`Composition ID: ${getText(idEl)}`);

    const nameEl = findCpl('ContentTitleText') || findCpl('ContentTitle');
    if (nameEl) lines.push(`Content Title: ${getText(nameEl)}`);

    const editRate = getCompositionEditRate(root, ns);
    if (editRate) {
      const editRateText = getText(editRate);
      const parsedRate = parseEditRate(editRateText);
      if (parsedRate) {
        lines.push(`Edit Rate: ${editRateText} (${formatFps(parsedRate[0], parsedRate[1])})`);
      } else {
        lines.push(`Edit Rate: ${editRateText}`);
      }
    }

    const bitDepthText = findBitDepth(root) || 'Not found';
    lines.push(`Bit Depth: ${bitDepthText}`);

    const colorPrimaries = ns ? findDesc(root, 'ColorPrimaries', ns.r1) : findDesc(root, 'ColorPrimaries', null);
    const colorPrimariesUl = colorPrimaries ? getText(colorPrimaries) : null;
    let description = colorPrimariesUl
      ? ulMappings[colorPrimariesUl] || colorPrimariesUl
      : 'Not found';
    lines.push(`Color Primaries: ${description}`);

    const cpWhitePoint = colorPrimariesUl
      ? colorPrimariesWhitePoint[colorPrimariesUl] || 'Unknown'
      : 'Not found';
    lines.push(`Color Primaries White Point: ${cpWhitePoint}`);

    const transfer = ns ? findDesc(root, 'TransferCharacteristic', ns.r1) : findDesc(root, 'TransferCharacteristic', null);
    const transferCharacteristicUl = transfer ? getText(transfer) : null;
    description = transferCharacteristicUl
      ? ulMappings[transferCharacteristicUl] || transferCharacteristicUl
      : 'Not found';
    lines.push(`Transfer Characteristic: ${description}`);

    const rec709Encoding = isRec709Encoding(colorPrimariesUl, transferCharacteristicUl);

    const masteringDisplay = findElementByName(root, 'MasteringDisplayPrimaries');
    if (masteringDisplay) {
      const primaries = [];
      for (const cp of findElementsByName(masteringDisplay, 'ColorPrimary')) {
        const xElem = findChildByName(cp, 'X');
        const yElem = findChildByName(cp, 'Y');
        if (xElem && yElem) {
          const xt = getText(xElem);
          const yt = getText(yElem);
          if (xt != null && yt != null) primaries.push(xt, yt);
        }
      }
      if (primaries.length === 6) {
        const whitePoint = findElementByName(root, 'MasteringDisplayWhitePointChromaticity');
        if (whitePoint) {
          const xElem = findChildByName(whitePoint, 'X');
          const yElem = findChildByName(whitePoint, 'Y');
          if (xElem && yElem) {
            const wx = getText(xElem);
            const wy = getText(yElem);
            if (wx != null && wy != null) {
              primaries.push(wx, wy);
              const gamutLabel = gamutLabelForFullKey(primaries);
              const wpx = primaries[6];
              const wpy = primaries[7];
              const whiteLabel = whitePointChromaticity.get(`${wpx},${wpy}`) || 'Unknown';
              lines.push(
                `Mastering Display Primaries: ${gamutLabel} -- R(${primaries[0]},${primaries[1]}) G(${primaries[2]},${primaries[3]}) B(${primaries[4]},${primaries[5]})`
              );
              lines.push(`Mastering Display White Point: ${whiteLabel} -- (${wpx},${wpy})`);
            } else pushMasteringDisplayMissing(lines, rec709Encoding);
          } else pushMasteringDisplayMissing(lines, rec709Encoding);
        } else pushMasteringDisplayMissing(lines, rec709Encoding);
      } else pushMasteringDisplayMissing(lines, rec709Encoding);
    } else pushMasteringDisplayMissing(lines, rec709Encoding);

    lines.push('');
    lines.push('Top-level Elements:');
    const skip = new Set([
      'ContentVersionList',
      'EssenceDescriptorList',
      'ExtensionProperties',
      'SegmentList',
      'LocaleList',
      'EditRate',
    ]);
    for (let i = 0; i < root.children.length; i++) {
      const child = root.children[i];
      const tagName = child.localName;
      if (skip.has(tagName)) continue;
      const txt = getText(child);
      if (txt) {
        let t = txt;
        if (t.length > 80) t = t.slice(0, 77) + '...';
        lines.push(`  ${tagName}: ${t}`);
      } else lines.push(`  ${tagName}: (has sub-elements)`);
    }

    lines.push('');
    const j2kProfile = extractJ2kEncodingProfile(root, ns);
    if (j2kProfile) {
      lines.push('IMF Encoding Profile:');
      if (j2kProfile.isHtj2k) {
        lines.push('  Mainlevel: n/a');
        lines.push('  Sublevel: n/a');
      } else {
        if ('mainlevel' in j2kProfile) lines.push(`  Mainlevel: ${j2kProfile.mainlevel}`);
        if ('sublevel' in j2kProfile) lines.push(`  Sublevel: ${j2kProfile.sublevel}`);
      }
      lines.push(`  Encoding: ${j2kProfile.encoding}`);
      lines.push(`  Disney Mastering Spec: ${disneySpecReportLabel(j2kProfile.disneySpec)}`);
    } else {
      lines.push('IMF Encoding Profile: Not found');
    }

    lines.push('');
    const resolutionSummary = collectResolutionConsistency(root);
    lines.push('Resolution:');
    if (!resolutionSummary) {
      lines.push('  Not found');
    } else {
      let hasResolutionWarning = false;
      for (let ri = 0; ri < resolutionSummary.length; ri++) {
        if (resolutionSummary[ri].warning) hasResolutionWarning = true;
      }
      lines.push(`  Resolution Consistency: ${hasResolutionWarning ? 'Warning' : 'Pass'}`);
      for (let ri = 0; ri < resolutionSummary.length; ri++) {
        const item = resolutionSummary[ri];
        lines.push(`  ${item.label}: ${item.displayValue}`);
        if (item.warning) lines.push(`  ${item.warning}`);
      }
    }

    lines.push('');
    const segmentList = findCpl('SegmentList');
    if (segmentList) {
      let mainImageResources = 0;
      let totalFrames = 0;
      for (let si = 0; si < segmentList.children.length; si++) {
        const segment = segmentList.children[si];
        const sequenceList = findSequenceListOnSegment(segment, ns);
        if (!sequenceList) continue;
        const mainImageSeq = findMainImageSequence(sequenceList, ns);
        if (!mainImageSeq) continue;
        const cplUri = ns ? ns.cpl : null;
        const resourceList = ns
          ? findDesc(mainImageSeq, 'ResourceList', cplUri)
          : findDesc(mainImageSeq, 'ResourceList', null);
        if (!resourceList) continue;
        const resources = ns
          ? findAllDesc(resourceList, 'Resource', cplUri)
          : findAllDesc(resourceList, 'Resource', null);
        mainImageResources += resources.length;
        for (const res of resources) {
          const srcDur = ns ? findChild(res, 'SourceDuration', cplUri) : findChild(res, 'SourceDuration', null);
          const st = srcDur ? getText(srcDur) : '';
          if (st && /^\d+$/.test(st)) totalFrames += parseInt(st, 10);
        }
      }
      if (mainImageResources > 0) {
        lines.push(`Number of shots in main image sequence: ${mainImageResources}`);
        const editRateElem = getCompositionEditRate(root, ns);
        if (editRateElem) {
          const parts = (getText(editRateElem) || '').trim().split(/\s+/);
          try {
            if (parts.length === 2) {
              const fpsNum = parseInt(parts[0], 10);
              const fpsDen = parseInt(parts[1], 10);
              const rate = fpsNum / fpsDen;
              const totalSeconds = rate !== 0 ? totalFrames / rate : 0;
              let hours = Math.floor(totalSeconds / 3600);
              let minutes = Math.floor((totalSeconds % 3600) / 60);
              let secondsWhole = Math.floor(totalSeconds % 60);
              const fractional = totalSeconds - Math.floor(totalSeconds);
              let frames = Math.round(fractional * rate);
              if (frames >= Math.round(rate)) {
                frames = 0;
                secondsWhole += 1;
                if (secondsWhole >= 60) {
                  secondsWhole = 0;
                  minutes += 1;
                  if (minutes >= 60) {
                    minutes = 0;
                    hours += 1;
                  }
                }
              }
              lines.push(
                `Total run time: ${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(secondsWhole).padStart(2, '0')}:${String(frames).padStart(2, '0')} (hh:mm:ss:frames)`
              );
              lines.push(`Total duration (frames): ${totalFrames}`);
              lines.push('');
              lines.push(
                `ISXD track present: ${hasIsxdTrack(root) ? 'present' : 'not present'}`
              );
              const extensionProperties = findElementByName(root, 'ExtensionProperties');
              let maxCllElem = null;
              let maxFallElem = null;
              if (extensionProperties) {
                maxCllElem = findElementByName(extensionProperties, 'MaxCLL');
                maxFallElem = findElementByName(extensionProperties, 'MaxFALL');
              }
              const maxCllText =
                maxCllElem && getText(maxCllElem) && String(getText(maxCllElem)).trim()
                  ? String(getText(maxCllElem)).trim()
                  : 'not present';
              const maxFallText =
                maxFallElem && getText(maxFallElem) && String(getText(maxFallElem)).trim()
                  ? String(getText(maxFallElem)).trim()
                  : 'not present';
              const maxValuesPresent =
                maxCllText !== 'not present' || maxFallText !== 'not present';
              lines.push('');
              lines.push(`Max Values: ${maxValuesPresent ? 'present' : 'not present'}`);
              lines.push(`  MaxCLL: ${maxCllText}`);
              lines.push(`  MaxFALL: ${maxFallText}`);
            } else lines.push('Edit rate not in expected numerator denominator format.');
          } catch (e) {
            lines.push('Edit rate not found or invalid for run time calculation.');
          }
        } else lines.push('Edit rate not found for run time calculation.');
      } else lines.push('Main image sequence resources: Not found');
    } else lines.push('SegmentList: Not found');

    lines.push('');
    if (segmentList) {
      const audioTracks = [];
      const soundfields = new Set();
      const languages = new Set();
      for (let si = 0; si < segmentList.children.length; si++) {
        const segment = segmentList.children[si];
        const sequenceList = findSequenceListOnSegment(segment, ns);
        if (!sequenceList) continue;
        const mainAudioSeqs = findAllMainAudioSequences(sequenceList, ns);
        for (const audioSeq of mainAudioSeqs) {
          audioTracks.push(audioSeq);
          const cplUri = ns ? ns.cpl : null;
          const resourceList = ns
            ? findDesc(audioSeq, 'ResourceList', cplUri)
            : findDesc(audioSeq, 'ResourceList', null);
          if (!resourceList) continue;
          const resources = ns
            ? findAllDesc(resourceList, 'Resource', cplUri)
            : findAllDesc(resourceList, 'Resource', null);
          for (const resource of resources) {
            const sourceEncoding = ns
              ? findDesc(resource, 'SourceEncoding', cplUri)
              : findDesc(resource, 'SourceEncoding', null);
            if (!sourceEncoding || !getText(sourceEncoding)) continue;
            const encodingId = getText(sourceEncoding);
            const essenceDesc = findEssenceDescriptorById(root, ns, encodingId);
            if (!essenceDesc) continue;
            let soundfieldGroup = ns
              ? findDesc(essenceDesc, 'SoundfieldGroupLabelSubDescriptor', ns.r0)
              : null;
            if (!soundfieldGroup) {
              for (const el of walkElements(essenceDesc)) {
                if (el.localName === 'SoundfieldGroupLabelSubDescriptor') {
                  soundfieldGroup = el;
                  break;
                }
              }
            }
            if (!soundfieldGroup) continue;
            let tagSf = null;
            let language = null;
            if (ns) {
              tagSf = findDesc(soundfieldGroup, 'MCATagName', ns.r1);
              language = findDesc(soundfieldGroup, 'RFC5646SpokenLanguage', ns.r1);
            } else {
              for (const el of walkElements(soundfieldGroup)) {
                if (el.localName === 'MCATagName') tagSf = el;
                else if (el.localName === 'RFC5646SpokenLanguage') language = el;
              }
            }
            if (tagSf && getText(tagSf)) soundfields.add(getText(tagSf));
            if (language && getText(language)) languages.add(getText(language));
          }
        }
      }
      if (audioTracks.length) {
        lines.push(`Number of audio tracks: ${audioTracks.length}`);
        if (soundfields.size) lines.push(`Soundfields present: ${Array.from(soundfields).sort().join(', ')}`);
        if (languages.size) {
          const sortedLangs = Array.from(languages).sort();
          lines.push(`Languages present: ${sortedLangs.join(', ')}`);
          const eltCodes = sortedLangs.map(formatDisneyEltForTag);
          lines.push(`Disney ELT language codes: ${eltCodes.join(', ')}`);
        }
      } else lines.push('Audio tracks: Not found');
    }

    lines.push('');
    return lines.join('\n');
  }

  function parseCplDocumentResult(xmlString, displayName) {
    const parser = new DOMParser();
    const doc = parser.parseFromString(xmlString, 'application/xml');
    const parserErr = doc.querySelector('parsererror');
    if (parserErr) {
      const msg = (parserErr.textContent || '').trim() || 'Invalid XML';
      return { success: false, fatalErrors: [msg], warnings: [], report: null };
    }
    const root = doc.documentElement;
    if (!root) {
      return { success: false, fatalErrors: ['Empty document'], warnings: [], report: null };
    }
    const rootTagOk =
      root.localName === 'CompositionPlaylist' ||
      (root.tagName && String(root.tagName).endsWith('CompositionPlaylist'));
    if (!rootTagOk) {
      return {
        success: false,
        fatalErrors: [
          'This file does not appear to be an IMF Composition Playlist (CPL) XML file.',
          'Please ensure you are parsing a valid IMF CPL file.',
        ],
        warnings: [],
        report: null,
      };
    }
    const ns = buildNsFromRoot(root);
    const warnings = collectCplWarnings(root, ns);
    let report;
    try {
      report = buildReport(displayName || 'uploaded.xml', root, ns);
    } catch (e) {
      return {
        success: false,
        fatalErrors: [String((e && e.message) || e)],
        warnings,
        report: null,
      };
    }
    return { success: true, fatalErrors: [], warnings, report };
  }

  function parseCplXmlString(xmlString, displayName) {
    const r = parseCplDocumentResult(xmlString, displayName);
    if (!r.success) throw new Error(r.fatalErrors.join('\n'));
    return r.report;
  }

  function escapeHtmlUi(s) {
    const d = document.createElement('div');
    d.textContent = s;
    return d.innerHTML;
  }

  const IMF_CPL_UI_BOUND = 'data-imf-cpl-parser-bound';

  /** Web2: validation + report (no inline script - works when CMS strips embedded scripts). */
  function initWeb2Ui() {
    const form = document.getElementById('cpl-form');
    if (form && form.getAttribute(IMF_CPL_UI_BOUND) === 'true') return;
    const fileInput = document.getElementById('cpl-file');
    const validationEl = document.getElementById('validation');
    const reportEl = document.getElementById('report');
    const submitBtn = document.getElementById('submit-btn');
    if (!form || !fileInput || !validationEl || !reportEl || !submitBtn) return;

    function renderValidation(result) {
      if (!result.success) {
        validationEl.innerHTML =
          '<span class="badge fail">Failed</span><ul class="checks fatal">' +
          result.fatalErrors.map(function (e) {
            return '<li>' + escapeHtmlUi(e) + '</li>';
          }).join('') +
          '</ul>';
        return;
      }
      let html = '<span class="badge ok">CPL summarized</span>';
      html +=
        '<p>Root element is CompositionPlaylist; XML is well-formed.</p>';
      if (result.warnings && result.warnings.length) {
        html +=
          '<p><strong>Warnings</strong> (non-fatal; report may still be partial):</p><ul class="checks warn">';
        html += result.warnings
          .map(function (w) {
            return '<li>' + escapeHtmlUi(w) + '</li>';
          })
          .join('');
        html += '</ul>';
      }
      validationEl.innerHTML = html;
    }

    function runAnalyze(ev) {
      if (ev) {
        ev.preventDefault();
        ev.stopPropagation();
      }
      const file = fileInput.files && fileInput.files[0];
      if (!file) return;

      reportEl.classList.add('placeholder');
      reportEl.textContent = 'Analyzing...';
      validationEl.innerHTML = '<span class="badge fail">...</span> Running checks...';
      submitBtn.disabled = true;

      const reader = new FileReader();
      reader.onload = function () {
        try {
          const text = String(reader.result || '');
          const result = parseCplDocumentResult(text, file.name);
          renderValidation(result);
          if (result.success && result.report) {
            reportEl.classList.remove('placeholder');
            reportEl.textContent = result.report;
          } else {
            reportEl.classList.add('placeholder');
            reportEl.textContent = result.success
              ? 'No report.'
              : 'Fix fatal errors above to generate a report.';
          }
        } catch (err) {
          validationEl.innerHTML =
            '<span class="badge fail">Error</span><p class="fatal">' +
            escapeHtmlUi(err && err.message ? err.message : String(err)) +
            '</p>';
          reportEl.classList.add('placeholder');
          reportEl.textContent = '';
        } finally {
          submitBtn.disabled = false;
        }
      };
      reader.onerror = function () {
        validationEl.innerHTML =
          '<span class="badge fail">Failed</span><p class="fatal">Could not read the file.</p>';
        reportEl.textContent = '';
        submitBtn.disabled = false;
      };
      reader.readAsText(file, 'UTF-8');
    }

    form.addEventListener('submit', function (e) {
      e.preventDefault();
      e.stopPropagation();
      runAnalyze(e);
    });
    submitBtn.addEventListener('click', function (e) {
      e.preventDefault();
      e.stopPropagation();
      runAnalyze(e);
    });
    form.setAttribute(IMF_CPL_UI_BOUND, 'true');
  }

  /** Web1: single report panel. */
  function initWeb1Ui() {
    const form = document.getElementById('cpl-form');
    if (form && form.getAttribute(IMF_CPL_UI_BOUND) === 'true') return;
    const fileInput = document.getElementById('cpl-file');
    const message = document.getElementById('message');
    const submitBtn = document.getElementById('submit-btn');
    if (!form || !fileInput || !message || !submitBtn) return;
    if (document.getElementById('validation')) return;

    function runAnalyze(ev) {
      if (ev) {
        ev.preventDefault();
        ev.stopPropagation();
      }
      const file = fileInput.files && fileInput.files[0];
      if (!file) return;

      message.classList.remove('error', 'placeholder');
      message.textContent = 'Analyzing...';
      submitBtn.disabled = true;

      const reader = new FileReader();
      reader.onload = function () {
        try {
          const text = String(reader.result || '');
          const report = parseCplXmlString(text, file.name);
          message.classList.remove('error');
          message.textContent = report;
        } catch (err) {
          message.classList.add('error');
          message.textContent = err && err.message ? err.message : String(err);
        } finally {
          submitBtn.disabled = false;
        }
      };
      reader.onerror = function () {
        message.classList.add('error');
        message.textContent = 'Could not read the file.';
        submitBtn.disabled = false;
      };
      reader.readAsText(file, 'UTF-8');
    }

    form.addEventListener('submit', function (e) {
      e.preventDefault();
      e.stopPropagation();
      runAnalyze(e);
    });
    submitBtn.addEventListener('click', function (e) {
      e.preventDefault();
      e.stopPropagation();
      runAnalyze(e);
    });
    form.setAttribute(IMF_CPL_UI_BOUND, 'true');
  }

  function autoInitEmbeddedUi() {
    initWeb2Ui();
    initWeb1Ui();
  }

  /** CMS (e.g. Strapi + React) often mounts the widget after DOMContentLoaded; retry + observe subtree. */
  function tryAutoInitEmbeddedUi() {
    var tries = 0;
    var maxTries = 80;
    var delayMs = 120;
    function tick() {
      var form = document.getElementById('cpl-form');
      if (form && form.getAttribute(IMF_CPL_UI_BOUND) === 'true') return;
      autoInitEmbeddedUi();
      form = document.getElementById('cpl-form');
      if (form && form.getAttribute(IMF_CPL_UI_BOUND) === 'true') return;
      tries += 1;
      if (tries < maxTries) setTimeout(tick, delayMs);
    }
    tick();
  }

  function observeCplFormMount() {
    if (typeof MutationObserver === 'undefined' || !document.documentElement) return;
    var scheduled = false;
    function run() {
      var form = document.getElementById('cpl-form');
      if (!form || form.getAttribute(IMF_CPL_UI_BOUND) === 'true') return;
      autoInitEmbeddedUi();
    }
    function schedule() {
      if (scheduled) return;
      scheduled = true;
      requestAnimationFrame(function () {
        scheduled = false;
        run();
      });
    }
    var mo = new MutationObserver(schedule);
    mo.observe(document.documentElement, { childList: true, subtree: true });
  }

  //SIP-842
  function isSafeToBoot() {
    try {
      const path = String((typeof window !== 'undefined' && window.location && window.location.pathname) || '').toLowerCase();
      if (path.indexOf('/admin') >= 0 || path.indexOf('content-manager') >= 0) return false;
      const embed = document.querySelector('[data-cpl-strapi], [data-imf-js]');
      if (embed instanceof HTMLElement && embed.closest('[contenteditable="true"], .ck-editor, .ck-editor__editable, .ck-content')) return false;
    } catch (e) {
      return false;
    }
    return true;
  }

  //SIP-842
  function isLocalPreviewPage() {
    return Boolean(
      document.getElementById('cpl-form') &&
      !document.querySelector('[data-cpl-strapi], [data-imf-js]')
    );
  }

  //SIP-842: local preview only; embeds boot via autoInitEmbeddedUi()
  if (typeof document !== 'undefined' && isLocalPreviewPage() && isSafeToBoot()) {
    function boot() {
      tryAutoInitEmbeddedUi();
      observeCplFormMount();
    }
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
    else boot();
  }

  window.ImfCplParser = {
    parseCplXmlString,
    parseCplDocumentResult,
    buildReport,
    collectCplWarnings,
    buildNsFromRoot,
    hasIsxdTrack,
    autoInitEmbeddedUi,
  };
})();